/**
 * <s-variant-picker> — Listens for option `change` events and swaps the product form
 * and title via partial rendering, keeping price and add-to-cart state in
 * sync with the selected variant without a full page reload.
 *
 * Required child markup:
 *   [data-variant-id]           — On each swatch/radio/select option input; value used to
 *                                 build the ?variant= URL param and trigger the partial fetch.
 *   select > option[data-variant-id] — Alternative: variant id carried on <option> elements
 *                                       inside a <select> (read via selectedOptions[0].dataset).
 *   input[name="id"]            — Hidden product-form input kept in sync for add-to-cart.
 *   input[name="quantity"]      — Quantity input preserved across product-form swaps.
 *
 * Element dataset (on <s-variant-picker>):
 *   data-updated-message   — i18n string announced via #sr-announcer after the variant updates.
 *   data-error-message     — i18n string shown locally and announced after a current update fails.
 *
 * Optional child markup:
 *   [data-variant-picker-error] — Visible error owned by the picker; add-to-cart errors remain
 *                                 owned by [data-add-to-cart-error].
 *
 * Partial contract:
 *   "product-form"   — must match {% partial "product-form" %} in templates/product.liquid.
 *   "product-title"  — must match {% partial "product-title" %} in templates/product.liquid.
 *                      Renaming either side silently breaks variant switching.
 *
 * External coupling:
 *   history.replaceState                  — ?variant= URL param updated on every selection.
 *   #sr-announcer (global)                — Live region announced with success/error feedback.
 *   aria-busy (on self)                   — Set while the current variant update is pending.
 */
// <s-variant-picker> listens for option changes and swaps the product form +
// title via partial rendering. This keeps price and the add-to-cart
// button in sync with the selected variant without a full page reload.
import { partials } from '@shopify/partial-rendering';
import { ProductSelectEvent } from '@shopify/standard-events';
import { transition } from '@theme/view-transition';
import { announceGlobalLiveRegion } from '@theme/live-region';

const PRODUCT_VIEW_PAYLOAD_SELECTOR = 'script[type="application/json"][data-product-view-payload]';
const ACTIVE_UPDATE = Symbol('s-variant-picker-active-update');

// --- Keep app-injected fields (e.g. Live Product Options) across the product-form partial swap ---
// Apps add their fields into the server-rendered form after load; swapping the partial would drop them
// (and anything the shopper typed). We detach those foreign nodes before the swap and re-insert them
// next to the same theme element afterwards, so their state and listeners survive.
function themeKey(el) {
  if (el.dataset?.themeOwn) return `own:${el.dataset.themeOwn}`;
  if (el.matches('s-variant-picker')) return 'picker';
  if (el.matches('[data-add-to-cart-error]')) return 'error';
  if (el.matches('form')) return 'form';
  if (el.matches('input[name], select[name], textarea[name]') && !el.name.startsWith('properties')) return `field:${el.name}`;
  if (el.matches('button.button-primary, a.button-primary, button[name="add"]')) return 'button';
  return `${el.tagName}#${el.id}.${el.getAttribute('class') || ''}`;
}

/** True when an app has hidden this theme element (inline style, hidden attribute or CSS). */
function isAppHidden(el) {
  if (el.hidden || el.style.display === 'none') return true;
  return getComputedStyle(el).display === 'none';
}

/**
 * Carry over what an app did to the stale theme element (hide it or parts of it) onto the fresh copy,
 * so e.g. Live Product Options keeps the native variant buttons hidden after a variant switch.
 */
function carryAppState(stale, fresh) {
  const pairs = [[stale, fresh]];
  const staleAll = stale.querySelectorAll('*');
  const freshAll = fresh.querySelectorAll('*');
  if (staleAll.length === freshAll.length) staleAll.forEach((node, i) => pairs.push([node, freshAll[i]]));
  for (const [from, to] of pairs) {
    if (from.hasAttribute('style')) to.setAttribute('style', from.getAttribute('style'));
    if (from.hidden) to.hidden = true;
  }
}

/**
 * Update the live variant picker in place from the freshly rendered one instead of replacing it,
 * so apps that listen to the native option inputs (Live Product Options conditional logic) keep
 * their listeners and references. Copies attributes, checked/selected/disabled state and leaf text;
 * never touches style/hidden (apps use those to hide the native picker). Returns false when the
 * structure differs (then the caller falls back to replacing).
 */
function morphInPlace(stale, fresh) {
  const staleAll = [stale, ...stale.querySelectorAll('*')];
  const freshAll = [fresh, ...fresh.querySelectorAll('*')];
  if (staleAll.length !== freshAll.length) return false;
  if (staleAll.some((node, i) => node.tagName !== freshAll[i].tagName)) return false;

  const APP_OWNED = new Set(['style', 'hidden']);
  const BOOLEAN_STATE = ['checked', 'selected', 'disabled'];
  staleAll.forEach((to, i) => {
    const from = freshAll[i];
    for (const { name, value } of from.attributes) {
      if (APP_OWNED.has(name)) continue;
      if (to.getAttribute(name) !== value) to.setAttribute(name, value);
    }
    for (const name of BOOLEAN_STATE) {
      if (!from.hasAttribute(name) && to.hasAttribute(name)) to.removeAttribute(name);
      if (name in to) to[name] = from.hasAttribute(name);
    }
    if (from.children.length === 0 && to.children.length === 0 && to.textContent !== from.textContent) {
      to.textContent = from.textContent;
    }
  });
  return true;
}

function findForm(host) {
  return host.querySelector('form.pdp-form') || host.querySelector('form');
}

/** Before the swap: remember the live form (with app fields and listeners) and the host's children. */
function snapshotLevels(host) {
  return { form: findForm(host), hostChildren: [...host.children] };
}

/**
 * After the swap: keep the ORIGINAL form element (apps bind to it and their fields live in it) and move the
 * fresh theme-owned pieces (variant picker, id input, button, error…) into it in place of the stale ones.
 * Then put back any app nodes that sat beside the form inside the partial.
 */
function restoreForeign(host, snapshot) {
  const { form: oldForm, hostChildren } = snapshot;
  const newForm = findForm(host);

  if (oldForm && newForm && oldForm !== newForm) {
    const oldByKey = new Map();
    for (const child of [...oldForm.children]) {
      const key = themeKey(child);
      if (!oldByKey.has(key)) oldByKey.set(key, child);
    }
    let lastPlaced = null;
    for (const fresh of [...newForm.children]) {
      const stale = oldByKey.get(themeKey(fresh));
      if (stale) {
        if (themeKey(fresh) === 'picker' && (isAppHidden(stale) || morphInPlace(stale, fresh))) {
          // Never swap the native variant buttons: either an app has hidden them (keep its copy as-is),
          // or they were just updated in place so app listeners on the option inputs survive.
          oldByKey.delete(themeKey(fresh));
          fresh.remove();
          lastPlaced = stale;
          continue;
        }
        if (themeKey(fresh).startsWith('field:')) {
          // Keep the original input (apps hold references to it, e.g. Live Product Options reads input[name="id"]
          // for its conditional logic) and just bring its value up to date, telling listeners when it changed.
          const changed = stale.value !== fresh.value;
          stale.value = fresh.value;
          stale.setAttribute('value', fresh.value);
          oldByKey.delete(themeKey(fresh));
          fresh.remove();
          if (changed) {
            stale.dispatchEvent(new Event('input', { bubbles: true }));
            stale.dispatchEvent(new Event('change', { bubbles: true }));
          }
          lastPlaced = stale;
          continue;
        }
        carryAppState(stale, fresh);
        stale.replaceWith(fresh);
        oldByKey.delete(themeKey(fresh));
      } else if (lastPlaced) {
        lastPlaced.after(fresh); // a theme element that only exists for this variant
      } else {
        oldForm.prepend(fresh);
      }
      lastPlaced = fresh;
    }
    // Remaining stale theme pieces with no fresh counterpart (e.g. sold-out notice gone) are removed;
    // unknown (app) nodes are never in oldByKey's theme set unless they look theme-owned, so keep those.
    for (const [key, stale] of oldByKey) {
      if (key === 'picker' || key === 'error' || key === 'button' || key.startsWith('field:') || key.startsWith('own:')) stale.remove();
    }
    newForm.replaceWith(oldForm);
  }

  // App nodes placed next to the form (inside the partial) come back beside the same theme element.
  const newByKey = new Map();
  for (const child of host.children) if (!newByKey.has(themeKey(child))) newByKey.set(themeKey(child), child);
  hostChildren.forEach((node, index) => {
    if (node.isConnected || newByKey.has(themeKey(node))) return;
    let anchor = null;
    for (let i = index + 1; i < hostChildren.length; i++) {
      const counterpart = newByKey.get(themeKey(hostChildren[i]));
      if (counterpart) { anchor = counterpart; break; }
    }
    host.insertBefore(node, anchor);
  });
}

class VariantPicker extends HTMLElement {
  #ac;
  #abortController;
  #committedHistoryState;
  #committedSelection;
  #committedUrl;
  #committedVariantId;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const productForm = this.closest('[data-product-form]');

    // The server-rendered form is the committed state to restore if its replacement fails.
    this.#committedVariantId = this.#getSelectedVariantId(productForm);
    this.#committedUrl = location.href;
    this.#committedHistoryState = history.state;
    this.#committedSelection = this.#captureSelection();

    this.addEventListener('change', (e) => {
      const input = e.target;
      const variantId = input.dataset.variantId
        || input.selectedOptions?.[0]?.dataset?.variantId;
      if (variantId) {
        this.#update(variantId);
      }
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#abortController?.abort();
  }

  async #update(variantId) {
    const productForm = this.closest('[data-product-form]');
    const updateHost = productForm || this;

    // The stable product-form host coordinates picker instances replaced by their own partial.
    // Refresh URL/history only when no optimistic update is active, never from a stale candidate.
    if (!updateHost[ACTIVE_UPDATE]) {
      this.#committedUrl = location.href;
      this.#committedHistoryState = history.state;
    }
    updateHost[ACTIVE_UPDATE]?.abort();
    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    updateHost[ACTIVE_UPDATE] = controller;

    const quantityValue = this.#getQuantityValue(productForm);
    let focusCapture = null;
    this.#syncSelectedVariantId(variantId, productForm);
    this.#showError('');
    this.setAttribute('aria-busy', 'true');

    const deferredEvent = ProductSelectEvent.createPromise();
    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId || '',
          title: this.dataset.productTitle || '',
          handle: this.dataset.productHandle || '',
        },
        selectedOptions: this.#getSelectedOptions(),
        promise: deferredEvent.promise,
      })
    );

    const url = new URL(location.href);
    // ?variant is a Shopify platform contract — the server reads it to pre-select and render the
    // matching variant on page load / deep-link. Renaming this param breaks variant URL sharing.
    url.searchParams.set('variant', variantId);

    try {
      // Let the browser paint before URL update.
      await new Promise(resolve => setTimeout(resolve, 0));
      this.#throwIfSuperseded(controller, updateHost);
      history.replaceState(history.state, '', url);

      // 'product-form' and 'product-title' must match the {% partial %} names in
      // templates/product.liquid — changing either name here or there breaks variant switching.
      const html = await partials.fetch('product-form', 'product-title', { url: url.toString(), signal: controller.signal });
      this.#throwIfSuperseded(controller, updateHost);
      await transition(() => {
        this.#throwIfSuperseded(controller, updateHost);
        // Capture at replacement time so focus moved during the fetch is never stolen back.
        focusCapture = this.#captureFocus();
        const keptAppNodes = productForm ? snapshotLevels(productForm) : [];
        partials.apply(html);
        if (productForm) restoreForeign(productForm, keptAppNodes);
      });

      // Applying product-form disconnects this picker. Ownership, rather than its now-aborted
      // signal, distinguishes that expected replacement from a newer picker selection.
      if (updateHost[ACTIVE_UPDATE] !== controller) throw this.#abortError(controller);

      this.#syncSelectedVariantId(variantId, productForm);
      this.#syncQuantityValue(quantityValue, productForm);
      this.#restoreFocus(focusCapture, productForm);

      deferredEvent.resolve({ variant: this.#getSelectedVariantPayload(productForm) });

      if (this.isConnected) this.#commitState(variantId, url);
      announceGlobalLiveRegion(this.dataset.updatedMessage);
    } catch (error) {
      deferredEvent.reject(error);

      const superseded = updateHost[ACTIVE_UPDATE] !== controller;
      if (superseded || !this.isConnected) return;

      this.#restoreCommittedState(productForm);
      if (error?.name === 'AbortError') return;

      this.#showError(this.dataset.errorMessage);
      announceGlobalLiveRegion(this.dataset.errorMessage);
      console.error('[s-variant-picker] Partial fetch failed:', error);
    } finally {
      if (updateHost[ACTIVE_UPDATE] === controller) delete updateHost[ACTIVE_UPDATE];
      if (this.#abortController === controller) {
        this.#abortController = null;
        this.removeAttribute('aria-busy');
      }
    }
  }

  #getSelectedOptions() {
    return Array.from(
      this.querySelectorAll('select[name], input[type="radio"][name]:checked')
    ).map((control) => ({
      name: control.name,
      value: String(control.value ?? ''),
    }));
  }

  #captureSelection() {
    return Array.from(
      this.querySelectorAll('select[name], input[type="radio"][name]')
    ).map((control) => {
      if (control instanceof HTMLSelectElement) {
        return {
          control,
          selected: Array.from(control.options, (option) => option.selected),
        };
      }

      return { control, checked: control.checked };
    });
  }

  #restoreCommittedState(productForm) {
    for (const state of this.#committedSelection || []) {
      if (!this.contains(state.control)) continue;

      if (state.control instanceof HTMLSelectElement) {
        Array.from(state.control.options).forEach((option, index) => {
          option.selected = state.selected[index] || false;
        });
      } else {
        state.control.checked = state.checked;
      }
    }

    this.#syncSelectedVariantId(this.#committedVariantId, productForm);
    history.replaceState(this.#committedHistoryState, '', this.#committedUrl);
  }

  #commitState(variantId, url) {
    this.#committedVariantId = variantId;
    this.#committedUrl = url.toString();
    this.#committedHistoryState = history.state;
    this.#committedSelection = this.#captureSelection();
  }

  #captureFocus() {
    const activeElement = document.activeElement;
    if (!this.contains(activeElement)) return null;

    if (activeElement instanceof HTMLInputElement && activeElement.type === 'radio') {
      return {
        type: 'radio',
        name: activeElement.name,
        value: activeElement.value,
        control: activeElement,
      };
    }

    // Capture stable identity for select elements.
    if (activeElement instanceof HTMLSelectElement) {
      return {
        type: 'select',
        name: activeElement.name,
        control: activeElement,
      };
    }

    return null;
  }

  #restoreFocus(focusCapture, productForm = this.closest('[data-product-form]')) {
    if (!focusCapture || focusCapture.control.isConnected) return;
    if (document.activeElement !== document.body && document.activeElement !== document.documentElement) return;

    const newPicker = productForm?.querySelector('s-variant-picker');
    if (!newPicker) return;

    let candidates;
    if (focusCapture.type === 'radio') {
      candidates = newPicker.querySelectorAll('input[type="radio"][name]');
      candidates = [...candidates].filter((control) => (
        control.name === focusCapture.name && control.value === focusCapture.value
      ));
    } else {
      candidates = [...newPicker.querySelectorAll('select[name]')]
        .filter((control) => control.name === focusCapture.name);
    }

    candidates[0]?.focus();
  }

  #getSelectedVariantId(productForm = this.closest('[data-product-form]')) {
    const variantInput = productForm?.querySelector('input[name="id"]');
    return variantInput instanceof HTMLInputElement ? variantInput.value : '';
  }

  #syncSelectedVariantId(variantId, productForm = this.closest('[data-product-form]')) {
    const variantInput = productForm?.querySelector('input[name="id"]');
    if (variantInput instanceof HTMLInputElement) {
      if (String(variantInput.value) === String(variantId)) return;
      variantInput.value = variantId;
      variantInput.setAttribute('value', String(variantId));
      // Apps (e.g. Live Product Options conditional logic) listen for the selected variant changing.
      variantInput.dispatchEvent(new Event('input', { bubbles: true }));
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #showError(message) {
    const error = this.querySelector('[data-variant-picker-error]');
    if (!error) return;

    if (message) error.textContent = message;
    error.hidden = !message;
  }

  #throwIfSuperseded(controller, updateHost) {
    if (!controller.signal.aborted && updateHost[ACTIVE_UPDATE] === controller) return;
    throw this.#abortError(controller);
  }

  #abortError(controller) {
    const reason = controller.signal.reason;
    if (reason instanceof Error && reason.name === 'AbortError') return reason;
    return new DOMException('Variant update superseded', 'AbortError');
  }

  #getQuantityValue(productForm = this.closest('[data-product-form]')) {
    const quantityInput = productForm?.querySelector('input[name="quantity"]');
    return quantityInput instanceof HTMLInputElement ? quantityInput.value : null;
  }

  #syncQuantityValue(quantityValue, productForm = this.closest('[data-product-form]')) {
    if (!quantityValue) return;

    const quantityInput = productForm?.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = this.#clampQuantityValue(quantityValue, quantityInput);
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #clampQuantityValue(quantityValue, quantityInput) {
    const parsedValue = Number(quantityValue);
    if (!Number.isFinite(parsedValue)) return quantityValue;

    let clampedValue = parsedValue;
    const min = Number(quantityInput.min);
    const max = Number(quantityInput.max);

    if (quantityInput.min && Number.isFinite(min)) {
      clampedValue = Math.max(min, clampedValue);
    }
    if (quantityInput.max && Number.isFinite(max)) {
      clampedValue = Math.min(max, clampedValue);
    }

    return String(clampedValue);
  }

  #getSelectedVariantPayload(productForm = this.closest('[data-product-form]')) {
    const marker = productForm?.querySelector(PRODUCT_VIEW_PAYLOAD_SELECTOR);
    const payload = marker?.textContent?.trim();
    if (!payload) return null;

    let parsedPayload;
    try {
      parsedPayload = JSON.parse(payload);
    } catch (error) {
      console.error('[variant-picker] Failed to parse product view payload:', error);
      return null;
    }

    return parsedPayload?.product?.selectedVariant || null;
  }
}
customElements.define('s-variant-picker', VariantPicker);
